import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, ChefHat, CalendarDays, ArrowRight } from 'lucide-react';
import RecipeImage from '../components/RecipeImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function DashboardPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMenu, setCurrentMenu] = useState(null);

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const response = await fetch(`${API}/menus/organized`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Combine all menus for the menus state (used for demo logic)
        const allMenus = data.current
          ? [data.current, ...data.upcoming, ...data.history]
          : [...data.upcoming, ...data.history];
        setMenus(allMenus);
        // Set current menu only if it exists for this week
        setCurrentMenu(data.current || null);
      }
    } catch (error) {
      console.error('Error fetching menus:', error);
    } finally {
      setLoading(false);
    }
  };

  // MOCK DATA FOR TUTORIAL
  const MOCK_MENU = {
    menu_id: 'demo',
    title: 'Demo Menu',
    week_start: new Date().toISOString().split('T')[0], // Today as start
    week_end: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 days later
    total_calories: 14500,
    slots: DAYS.flatMap(day => [
      { day, meal_type: 'lunch', recipe_name: 'Grilled Salmon with Asparagus', calories: 650, recipe_image: 'https://images.pexels.com/photos/3763847/pexels-photo-3763847.jpeg' },
      { day, meal_type: 'dinner_main', recipe_name: 'Quinoa Salad Bowl', calories: 450, recipe_image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg' },
      { day, meal_type: 'dinner_secondary', recipe_name: 'Greek Yogurt Parfait', calories: 250, recipe_image: 'https://images.pexels.com/photos/1099680/pexels-photo-1099680.jpeg' }
    ])
  };

  const showDemo = !currentMenu && menus.length === 0 && !loading && user && !user.tutorial_pages_seen?.includes('dashboard');
  const displayMenu = showDemo ? MOCK_MENU : currentMenu;

  // Get today's day name
  const getTodayDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()];
  };

  const todayDay = getTodayDay();

  // Get slots for a specific day
  const getSlotsForDay = (day) => {
    if (!displayMenu) return { lunch: null, dinner_main: null, dinner_secondary: null };
    const slots = displayMenu.slots || [];
    return {
      lunch: slots.find(s => s.day === day && s.meal_type === 'lunch'),
      dinner_main: slots.find(s => s.day === day && s.meal_type === 'dinner_main'),
      dinner_secondary: slots.find(s => s.day === day && s.meal_type === 'dinner_secondary'),
    };
  };

  const todaySlots = getSlotsForDay(todayDay);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="font-accent text-2xl text-primary rotate-[-1deg] mb-2">
            ¡Hola, {user?.name?.split(' ')[0]}!
          </p>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-charcoal">
            {t('dashboard.title')}
          </h1>
        </div>

        {!displayMenu ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CalendarDays className="w-12 h-12 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-2xl text-charcoal mb-2">
              {t('dashboard.noMenus')}
            </h2>
            <p className="font-inter text-stone mb-8">
              {t('dashboard.noMenusHint')}
            </p>
            <Button
              data-testid="generate-first-menu-button"
              onClick={() => navigate('/menu/new')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('dashboard.generateMenu')}
            </Button>
          </div>
        ) : (
          <>
            {/* Today's Plan */}
            <Card className="bg-white rounded-3xl shadow-soft p-6 md:p-8 mb-8" data-testid="today-plan-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-manrope font-semibold text-xl text-charcoal flex items-center gap-2">
                  <span className="w-3 h-3 bg-primary rounded-full animate-pulse-soft"></span>
                  {t('dashboard.today')} - {t(`days.${todayDay}`)}
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => !showDemo && navigate(`/menu/${displayMenu.menu_id}`)}
                  className={`text-primary hover:text-primary/80 font-manrope font-semibold ${showDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="view-full-menu-button"
                  disabled={showDemo}
                >
                  {t('menu.edit')}
                  <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Lunch */}
                <div className="bg-muted/50 rounded-2xl p-4">
                  <p className="font-manrope font-semibold text-xs uppercase tracking-widest text-stone mb-3">
                    {t('dashboard.lunch')}
                  </p>
                  {todaySlots.lunch?.recipe_name ? (
                    <div className="flex items-center gap-3">
                      <RecipeImage
                        src={todaySlots.lunch?.recipe_image}
                        alt={todaySlots.lunch?.recipe_name}
                        containerClassName="w-14 h-14 rounded-xl"
                      />
                      <div>
                        <p className="font-manrope font-medium text-charcoal">{todaySlots.lunch?.recipe_name}</p>
                        {todaySlots.lunch?.calories && (
                          <p className="font-inter text-sm text-sage">{todaySlots.lunch?.calories} {t('dashboard.kcal')}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="font-inter text-stone italic">{t('menu.empty')}</p>
                  )}
                </div>

                {/* Dinner Main */}
                <div className="bg-muted/50 rounded-2xl p-4">
                  <p className="font-manrope font-semibold text-xs uppercase tracking-widest text-stone mb-3">
                    {t('dashboard.dinnerMain')}
                  </p>
                  {todaySlots.dinner_main?.recipe_name ? (
                    <div className="flex items-center gap-3">
                      <RecipeImage
                        src={todaySlots.dinner_main?.recipe_image}
                        alt={todaySlots.dinner_main?.recipe_name}
                        containerClassName="w-14 h-14 rounded-xl"
                      />
                      <div>
                        <p className="font-manrope font-medium text-charcoal">{todaySlots.dinner_main?.recipe_name}</p>
                        {todaySlots.dinner_main?.calories && (
                          <p className="font-inter text-sm text-sage">{todaySlots.dinner_main?.calories} {t('dashboard.kcal')}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="font-inter text-stone italic">{t('menu.empty')}</p>
                  )}
                </div>

                {/* Dinner Secondary */}
                <div className="bg-muted/50 rounded-2xl p-4">
                  <p className="font-manrope font-semibold text-xs uppercase tracking-widest text-stone mb-3">
                    {t('dashboard.dinnerSecondary')}
                  </p>
                  {todaySlots.dinner_secondary?.recipe_name ? (
                    <div className="flex items-center gap-3">
                      <RecipeImage
                        src={todaySlots.dinner_secondary?.recipe_image}
                        alt={todaySlots.dinner_secondary?.recipe_name}
                        containerClassName="w-14 h-14 rounded-xl"
                      />
                      <div>
                        <p className="font-manrope font-medium text-charcoal">{todaySlots.dinner_secondary?.recipe_name}</p>
                        {todaySlots.dinner_secondary?.calories && (
                          <p className="font-inter text-sm text-sage">{todaySlots.dinner_secondary?.calories} {t('dashboard.kcal')}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="font-inter text-stone italic">{t('menu.empty')}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Week Overview - Vertical List */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-manrope font-semibold text-xl text-charcoal">
                  {displayMenu.title}
                </h2>
                <span className="font-inter text-sm text-stone">
                  {displayMenu.week_start} - {displayMenu.week_end}
                </span>
              </div>

              <div className="bg-white rounded-2xl shadow-soft overflow-hidden divide-y divide-border/50">
                {DAYS.map((day) => {
                  const slots = getSlotsForDay(day);
                  const isToday = day === todayDay;

                  return (
                    <div
                      key={day}
                      className={`p-4 transition-all ${isToday
                        ? 'bg-primary/5 border-l-4 border-l-primary'
                        : 'hover:bg-muted/30'
                        }`}
                      data-testid={`day-card-${day}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
                        {/* Day name */}
                        <div className="md:w-28 flex-shrink-0">
                          <p className={`font-manrope font-semibold text-sm ${isToday ? 'text-primary' : 'text-charcoal'
                            }`}>
                            {t(`days.${day}`)}
                            {isToday && <span className="ml-2 text-xs bg-primary text-white px-2 py-0.5 rounded-full">{t('dashboard.today')}</span>}
                          </p>
                        </div>

                        {/* Meals */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm min-w-0 sm:flex-1">
                            <span className="font-inter text-stone text-xs uppercase w-32 flex-shrink-0">{t('dashboard.lunch')}</span>
                            <span className="font-inter text-charcoal truncate">{slots.lunch?.recipe_name || '-'}</span>
                          </div>
                          <div className="hidden sm:block w-px h-4 bg-border/50 flex-shrink-0"></div>
                          <div className="flex items-center gap-2 text-sm min-w-0 sm:flex-1">
                            <span className="font-inter text-stone text-xs uppercase w-32 flex-shrink-0 sm:hidden">{t('dashboard.dinnerMainShort')}</span>
                            <span className="hidden sm:inline font-inter text-stone text-xs uppercase flex-shrink-0">{t('dashboard.dinnerMain')}</span>
                            <span className="font-inter text-charcoal truncate">{slots.dinner_main?.recipe_name || '-'}</span>
                          </div>
                          <div className="hidden sm:block w-px h-4 bg-border/50 flex-shrink-0"></div>
                          <div className="flex items-center gap-2 text-sm min-w-0 sm:flex-1">
                            <span className="font-inter text-stone text-xs uppercase w-32 flex-shrink-0 sm:hidden">{t('dashboard.dinnerSecondaryShort')}</span>
                            <span className="hidden sm:inline font-inter text-stone text-xs uppercase flex-shrink-0">{t('dashboard.dinnerSecondary')}</span>
                            <span className="font-inter text-charcoal truncate">{slots.dinner_secondary?.recipe_name || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/menu/new')}
                className="h-auto py-6 rounded-2xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 flex flex-col items-center gap-2"
                data-testid="new-menu-button"
              >
                <Plus className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <span className="font-manrope font-semibold text-charcoal">{t('dashboard.generateMenu')}</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate('/recipes')}
                className="h-auto py-6 rounded-2xl border-2 border-border hover:border-sage hover:bg-sage/5 flex flex-col items-center gap-2"
                data-testid="view-recipes-button"
              >
                <ChefHat className="w-6 h-6 text-sage" strokeWidth={1.5} />
                <span className="font-manrope font-semibold text-charcoal">{t('nav.recipes')}</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate('/history')}
                className="h-auto py-6 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 flex flex-col items-center gap-2"
                data-testid="view-history-button"
              >
                <CalendarDays className="w-6 h-6 text-primary" strokeWidth={1.5} />
                <span className="font-manrope font-semibold text-charcoal">{t('menu.history')}</span>
              </Button>

              {displayMenu.total_calories && (
                <div className="py-6 rounded-2xl bg-sage/10 flex flex-col items-center justify-center gap-1">
                  <span className="font-manrope font-bold text-2xl text-sage">{displayMenu.total_calories}</span>
                  <span className="font-inter text-sm text-stone">{t('dashboard.weekTotal')} {t('dashboard.kcal')}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
